/**
 * PdfExportButton — Phase 7.3 reusable PDF export button.
 *
 * Renders an outline button with the `FileDown` icon, a tooltip warning
 * the 5,000-row cap, and a loading spinner while the request is in
 * flight. Sits next to the existing "Exportar Excel" button in each
 * inventory table.
 *
 * Tooltip uses a local TooltipProvider (same pattern as HelpTooltip).
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function PdfExportButton({
  onClick,
  exporting = false,
  disabled = false,
  hidden = false,
  testId = "pdf-export-btn",
}) {
  const { t } = useTranslation();

  if (hidden) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* span wrapper keeps the tooltip working when the button is disabled */}
          <span className="inline-flex">
            <Button
              variant="outline"
              size="sm"
              onClick={onClick}
              disabled={exporting || disabled}
              data-testid={testId}
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <FileDown className="h-3.5 w-3.5 mr-1.5" />
              )}
              {t("inventory.exportPdf.button", { defaultValue: "Exportar PDF" })}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {t("inventory.exportPdf.tooltip", {
            defaultValue: "Exportar a PDF (máx. 5,000 filas)",
          })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
